/**
 * SolarMap Component
 * 
 * A dedicated component for rendering solar renewable energy potential data
 * on an interactive Leaflet map with optimized performance.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import L from 'leaflet';
import { createMap, calculateMapBounds, fitMapBoundsAsHome } from '../../utils/mapUtils';
import { 
  optimizeGeoJSONData,
  buildRadialFillGeoJSON,
  getRadialFillStyle,
  RADIAL_OUTLINE_STYLE,
  createZonePopup, 
  getZoneStyle, 
  getHoverStyle
} from '../../utils/renewableUtils';

const SolarMap = ({ 
  solarData, 
  solarThresholds, 
  countryIso,
  className = "w-full h-full",
  layoutActive = true,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const lastBoundsCountryIsoRef = useRef(undefined);
  const boundsFitSessionRef = useRef(0);

  const clearSolarMapLayers = useCallback((map) => {
    if (!map) return;
    const layersToRemove = [];
    map.eachLayer((layer) => {
      if (
        layer instanceof L.GeoJSON &&
        (layer.options.solarLayer || layer.options.solarOutlineLayer)
      ) {
        layersToRemove.push(layer);
      }
    });
    if (layersToRemove.length > 0) {
      if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
      layersToRemove.forEach((layer) => map.removeLayer(layer));
    }
    map.options.boundsSet = false;
  }, []);

  // Create GeoJSON layer helper
  const createGeoJSONLayer = useCallback((geoJsonData, styleFunction, onEachFeature) => {
    const layer = L.geoJSON(geoJsonData, {
      style: styleFunction,
      onEachFeature: onEachFeature
    });
    
    return layer;
  }, []);

  // Render solar zone shapes - thresholds passed as parameter, not in dependencies
  const renderSolarZoneShapes = useCallback((map, gridData, thresholds) => {
    if (!map || !gridData || gridData.length === 0) {
      return;
    }
    
    // Remove existing solar layers (fill + optional outline from radial-fill mode)
    const layersToRemove = [];
    map.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON && (layer.options.solarLayer || layer.options.solarOutlineLayer)) {
        layersToRemove.push(layer);
      }
    });
    if (layersToRemove.length > 0) {
      if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
      layersToRemove.forEach((l) => map.removeLayer(l));
    }

    // Get current map bounds and zoom level for performance optimization
    let mapBounds, zoomLevel;
    try {
      mapBounds = map.getBounds();
      zoomLevel = map.getZoom();
    } catch (error) {
      console.warn('Could not get map bounds/zoom:', error);
      mapBounds = null;
      zoomLevel = 2;
    }

    // -----------------------------------------------------------------------
    // Radial-fill path (continuous / clustered mode)
    // Two layers: ghost outline (non-interactive) + scaled fill (interactive)
    // -----------------------------------------------------------------------
    if (thresholds?.mode === 'continuous') {
      const { outlines, fills } = buildRadialFillGeoJSON(gridData);
      if (fills.features.length === 0) return;

      // Ghost outline layer — just shows cell boundaries, no interaction
      const outlineLayer = L.geoJSON(outlines, {
        style: () => RADIAL_OUTLINE_STYLE,
        interactive: false,
      });
      outlineLayer.options.solarOutlineLayer = true;
      outlineLayer.addTo(map);

      // Scaled fill layer — interactive, carries colour and hover
      const fillLayer = createGeoJSONLayer(
        fills,
        (feature) => getRadialFillStyle(feature, thresholds),
        (feature, layer) => {
          layer.feature = feature;
          const zone = feature.properties;
          const tooltipContent = createZonePopup(zone, 'solar');

          layer.on('mouseover', (e) => {
            e.target.setStyle({ ...getHoverStyle(zoomLevel), fillOpacity: 0.95 });
            e.target.bringToFront();
            if (map._sharedTooltip) {
              map._sharedTooltip.setLatLng(e.latlng).setContent(tooltipContent).addTo(map);
            }
          });
          layer.on('mousemove', (e) => {
            if (map._sharedTooltip) map._sharedTooltip.setLatLng(e.latlng);
          });
          layer.on('mouseout', (e) => {
            if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
            e.target.setStyle(getRadialFillStyle(feature, thresholds));
          });
        }
      );
      fillLayer.options.solarLayer = true;
      fillLayer.options.lastZoom = map.getZoom();
      fillLayer.addTo(map);
      return;
    }

    // -----------------------------------------------------------------------
    // Standard path (discrete / non-clustered mode)
    // -----------------------------------------------------------------------
    const geoJsonData = optimizeGeoJSONData(gridData, mapBounds, zoomLevel);
    if (geoJsonData.features.length === 0) return;

    const geoJsonLayer = createGeoJSONLayer(
      geoJsonData,
      (feature) => getZoneStyle(feature, 'solar', thresholds, zoomLevel),
      (feature, layer) => {
        const zone = feature.properties;
        layer.feature = feature;
        const tooltipContent = createZonePopup(zone, 'solar');

        layer.on('mouseover', (e) => {
          e.target.setStyle(getHoverStyle(zoomLevel));
          e.target.bringToFront();
          if (map._sharedTooltip) {
            map._sharedTooltip
              .setLatLng(e.latlng)
              .setContent(tooltipContent)
              .addTo(map);
          }
        });
        layer.on('mousemove', (e) => {
          if (map._sharedTooltip) map._sharedTooltip.setLatLng(e.latlng);
        });
        layer.on('mouseout', (e) => {
          if (map._sharedTooltip) map.closeTooltip(map._sharedTooltip);
          e.target.setStyle({
            ...getZoneStyle(feature, 'solar', thresholds, zoomLevel),
            weight: zoomLevel <= 4 ? 0.5 : zoomLevel <= 6 ? 0.8 : zoomLevel <= 8 ? 1 : 1.2,
            opacity: 0.8,
            fillOpacity: 0.6,
            color: 'white',
            dashArray: '3',
          });
        });
      }
    );

    geoJsonLayer.options.solarLayer = true;
    geoJsonLayer.options.lastZoom = map.getZoom();
    geoJsonLayer.options.thresholds = thresholds;
    geoJsonLayer.addTo(map);
  }, [createGeoJSONLayer]); // Removed onZoneSelect and solarThresholds from dependencies

  // Initialize map immediately when component mounts (no dependencies)
  useEffect(() => {
    // Check if map already exists (from previous mount or external source)
    if (mapInstanceRef.current) {
      setMapReady(true);
      return;
    }
    
    if (!mapRef.current) {
      return;
    }
    
    // Wait for container to be ready and have dimensions
    let retryCount = 0;
    const maxRetries = 20; // Increased retries
    
    const initMap = () => {
      // Check if map was already created (by another effect run)
      if (mapInstanceRef.current) {
        setMapReady(true);
        return;
      }
      
      if (!mapRef.current) return;
      const container = mapRef.current;
      
      // Check if container has dimensions
      const hasDimensions = container.offsetWidth > 0 && container.offsetHeight > 0;
      if (!hasDimensions && retryCount < maxRetries) {
        retryCount++;
        // Retry on next frame if container doesn't have dimensions yet
        requestAnimationFrame(initMap);
        return;
      }
      
      // Create map even if dimensions check failed (after max retries)
      try {
        const map = createMap(container, {
          defaultStyle: 'CLEAN_MINIMAL',
          attributionControl: false
        });
        
        // Set initial view immediately (required before map can be used)
        map.setView([0, 0], 2, { animate: false });

        // Single shared tooltip for the whole map. Every feature reuses this
        // instance via mouseover/mousemove/mouseout, so only one tooltip can
        // ever be visible at a time (mirrors the simulator-map pattern).
        map._sharedTooltip = L.tooltip({
          sticky: true,
          direction: 'auto',
          offset: [15, -15],
          className: 'renewable-zone-tooltip'
        });

        mapInstanceRef.current = map;
        setMapReady(true); // Signal that map is ready
      } catch (error) {
        console.error('Error creating solar map:', error);
      }
    };
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(initMap);
    
    // Cleanup only on unmount - actually remove the map
    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current._vsMapInteraction?.destroy();
          mapInstanceRef.current._vsMapReset?.destroy();
          mapInstanceRef.current.stop();
          mapInstanceRef.current.remove();
        } catch (err) {
          console.warn('Error removing solar map:', err);
        }
        mapInstanceRef.current = null;
        setMapReady(false);
      }
    };
  }, []); // Empty deps - runs once on mount

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    clearSolarMapLayers(mapInstanceRef.current);
    lastBoundsCountryIsoRef.current = undefined;
  }, [countryIso, clearSolarMapLayers]);

  // Render layers only when BOTH data AND thresholds are ready
  useEffect(() => {
    const session = ++boundsFitSessionRef.current;

    // Wait for map to be ready
    if (!mapReady || !mapInstanceRef.current) {
      return;
    }
    if (!solarData?.grid_data?.length || !solarThresholds) {
      clearSolarMapLayers(mapInstanceRef.current);
      return;
    }
    
    // Wait for map to be loaded before rendering
    const renderLayers = () => {
      if (!mapInstanceRef.current) {
        return;
      }
      
      const map = mapInstanceRef.current;
      
      // Check if map is ready - try to get center, if it fails, wait
      let isMapReady = false;
      try {
        const center = map.getCenter();
        isMapReady = center && typeof center.lat === 'number' && typeof center.lng === 'number';
      } catch (error) {
        isMapReady = false;
      }
      
      if (!isMapReady) {
        // Map not ready yet, wait for it
        if (map.whenReady) {
          map.whenReady(() => {
            setTimeout(() => {
              renderLayers();
            }, 50);
          });
        } else {
          // Retry after a short delay
          setTimeout(() => {
            renderLayers();
          }, 100);
        }
        return;
      }
      
      // Both ready - render layer first, then fit bounds
      const gridData = solarData.grid_data;

      if (countryIso !== lastBoundsCountryIsoRef.current) {
        map.options.boundsSet = false;
      }
      
      // Render layer with both data and thresholds (render first)
      renderSolarZoneShapes(map, gridData, solarThresholds);
      
      // Fit bounds AFTER rendering (so layers are visible)
      if (!map.options.boundsSet) {
        // Calculate bounds from ALL data for initial view
        let coordinates = [];
        const pushGeometryCoordinates = (geometry) => {
          if (!geometry || !geometry.type || !geometry.coordinates) {
            return;
          }

          if (geometry.type === 'Polygon') {
            geometry.coordinates.forEach((ring) => {
              ring.forEach((coord) => {
                coordinates.push([coord[1], coord[0]]); // lat, lng
              });
            });
            return;
          }

          if (geometry.type === 'MultiPolygon') {
            geometry.coordinates.forEach((polygon) => {
              polygon.forEach((ring) => {
                ring.forEach((coord) => {
                  coordinates.push([coord[1], coord[0]]); // lat, lng
                });
              });
            });
          }
        };

        gridData.forEach((zone) => {
          // Prefer full geometry bounds for large/disjoint countries (e.g., USA)
          if (zone.geometry) {
            pushGeometryCoordinates(zone.geometry);
          } else if (zone.lat && zone.lng) {
            coordinates.push([zone.lat, zone.lng]);
          }
        });
        
        // Fit bounds if we have coordinates
        if (coordinates.length > 0) {
          try {
            const bounds = calculateMapBounds(coordinates, 0.1);
            // Wait for map to be fully ready before fitting bounds
            let retryCount = 0;
            const maxRetries = 30; // Maximum 3 seconds (30 * 100ms)
            
            const fitBoundsWhenReady = () => {
              if (session !== boundsFitSessionRef.current) {
                return;
              }
              if (retryCount >= maxRetries) {
                console.warn('Max retries reached for fitBounds, map may not be ready');
                return;
              }
              
              if (!map || !map._loaded || !map._panes || !map._panes.mapPane) {
                // Map not ready yet, retry
                retryCount++;
                setTimeout(fitBoundsWhenReady, 100);
                return;
              }
              
              try {
                // Check if map has a valid center (map is initialized)
                const center = map.getCenter();
                if (center && center.lat !== undefined && center.lng !== undefined) {
                  if (session !== boundsFitSessionRef.current) {
                    return;
                  }
                  fitMapBoundsAsHome(map, bounds, { padding: [20, 20], animate: false });
                  map.options.boundsSet = true;
                  lastBoundsCountryIsoRef.current = countryIso;
                } else {
                  // Map center not set yet, retry
                  retryCount++;
                  setTimeout(fitBoundsWhenReady, 100);
                }
              } catch (error) {
                // Map not ready, retry
                retryCount++;
                if (retryCount < maxRetries) {
                  setTimeout(fitBoundsWhenReady, 100);
                } else {
                  console.warn('Error fitting bounds after retries:', error);
                }
              }
            };
            
            // Start checking after a small delay to let layers render
            setTimeout(fitBoundsWhenReady, 200);
          } catch (error) {
            console.warn('Error fitting bounds:', error);
          }
        }
      }
    };
    
    // Use a small delay to ensure map is initialized
    const timer = setTimeout(() => {
      renderLayers();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [solarData, solarThresholds, renderSolarZoneShapes, mapReady, countryIso]);

  // Update layer colors when thresholds change (don't recreate layer)
  useEffect(() => {
    if (!mapInstanceRef.current || !solarThresholds) return;
    
    // Find existing solar layer
    let existingLayer = null;
    mapInstanceRef.current.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON && layer.options.solarLayer) {
        existingLayer = layer;
      }
    });
    
    if (existingLayer) {
      // Update colors of each feature individually
      const zoomLevel = mapInstanceRef.current.getZoom();
      existingLayer.eachLayer((featureLayer) => {
        if (featureLayer.feature) {
          const newStyle = getZoneStyle(featureLayer.feature, 'solar', solarThresholds, zoomLevel);
          featureLayer.setStyle(newStyle);
        }
      });
      
      // Update stored thresholds
      existingLayer.options.thresholds = solarThresholds;
    }
  }, [solarThresholds]); // Only when thresholds change (not data)

  useEffect(() => {
    if (!layoutActive || !mapInstanceRef.current) return undefined;
    const id = window.requestAnimationFrame(() => {
      const map = mapInstanceRef.current;
      if (!map) return;
      map.invalidateSize();
      // After being hidden (e.g. simulator), tiles may not repaint until a pan/zoom
      if (typeof map.redraw === 'function') {
        map.redraw();
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [layoutActive]);

  return (
    <div className={className}>
      <div 
        ref={mapRef}
        className="w-full h-full min-h-96"
      />
      
      {/* Loading and error overlays are handled by parent component */}
    </div>
  );
};

export default SolarMap;
